// Battle List igual à do cliente: filtros no topo e, para cada criatura, o retrato,
// o nome e a barra de vida colorida pela porcentagem.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Outfit } from '../../../../shared/src/protocol';
import { renderOutfit } from '../outfit';
import { Widget } from './Widget';

const SKIN = '/assets/interface/images/skin/classic';

export interface BattleEntry { id: number | string; name: string; hpPct: number; outfit: Outfit; kind: 'monster' | 'player' | 'npc'; target?: boolean; skull?: 'white' | 'red' | 'black' }

/** Cores da barra de vida do Tibia por faixa de porcentagem. */
export function hpColor(p: number): string {
  if (p > 92) return '#00bc00';
  if (p > 60) return '#50a150';
  if (p > 30) return '#a1a100';
  if (p > 8) return '#bf0a0a';
  return '#910f0f';
}

const portraitCache = new Map<string, Promise<string>>();
function portrait(outfit: Outfit, size = 20): Promise<string> {
  const key = JSON.stringify(outfit) + ':' + size;
  let p = portraitCache.get(key);
  if (!p) {
    p = renderOutfit(outfit).then((c) => {
      // Recorta a parte desenhada do sprite e encaixa no quadrado de 20x20, como no cliente.
      const ctx = c.getContext('2d')!;
      const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
      let x0 = width, y0 = height, x1 = 0, y1 = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3]! > 0) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
      }
      const out = document.createElement('canvas');
      out.width = out.height = size;
      if (x1 >= x0) {
        const w = x1 - x0 + 1, h = y1 - y0 + 1, s = Math.min(size >= 32 ? 2 : 1, size / Math.max(w, h));
        const g = out.getContext('2d')!; g.imageSmoothingEnabled = false;
        g.drawImage(c, x0, y0, w, h, (size - w * s) / 2, (size - h * s) / 2, w * s, h * s);
      }
      return out.toDataURL();
    });
    portraitCache.set(key, p);
  }
  return p;
}

export function Portrait(props: { outfit: Outfit; size?: number; title?: string }) {
  const size = props.size ?? 20;
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => { let ok = true; portrait(props.outfit, size).then((s) => ok && setSrc(s)).catch(() => {}); return () => { ok = false; }; }, [JSON.stringify(props.outfit), size]);
  return src ? <img src={src} width={size} height={size} alt="" title={props.title} /> : <span style={{ display: 'inline-block', width: size + 'px', height: size + 'px' }} />;
}

type Filter = 'players' | 'npc' | 'monster' | 'skull' | 'party';
const FILTERS: Array<[Filter, string]> = [
  ['players', 'Esconder jogadores'], ['npc', 'Esconder NPCs'], ['monster', 'Esconder monstros'],
  ['skull', 'Esconder jogadores sem skull'], ['party', 'Esconder membros da party'],
];

export function BattleList(props: { entries: BattleEntry[]; onClick?: (id: string | number) => void; onLook?: (id: string | number) => void }) {
  const [hidden, setHidden] = useState<Set<Filter>>(new Set());
  const toggle = (f: Filter) => setHidden((h) => { const n = new Set(h); n.has(f) ? n.delete(f) : n.add(f); return n; });
  const list = props.entries.filter((e) =>
    !(e.kind === 'player' && hidden.has('players')) && !(e.kind === 'player' && hidden.has('skull') && !e.skull) && !(e.kind === 'npc' && hidden.has('npc')) && !(e.kind === 'monster' && hidden.has('monster')));
  const header = useRef<HTMLDivElement>(null);
  // altura da lista ajustável pelo jogador (só na vertical), arrastando a borda de baixo como uma janela; fica salva no navegador
  const [h, setH] = useState(() => { try { return Number(localStorage.getItem('bl-height')) || 120; } catch { return 120; } });
  const startDrag = (e: PointerEvent) => {
    e.preventDefault();
    const y0 = e.clientY, h0 = h;
    let last = h0;
    const move = (ev: PointerEvent) => { last = Math.max(50, Math.min(900, h0 + ev.clientY - y0)); setH(last); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); try { localStorage.setItem('bl-height', String(Math.round(last))); } catch { /* sem armazenamento */ } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  return (
    <Widget
      id="battle" title="Battle List" icon={`${SKIN}/icon-battlelist.png`}
      headerExtra={
        <div class="bl-filters" ref={header}>
          {FILTERS.map(([f, label]) => (
            <button key={f} title={label} class={'bl-filter' + (hidden.has(f) ? ' on' : '')} onClick={() => toggle(f)}>
              <img src={`${SKIN}/icon-battlelist-${f === 'monster' ? 'monster' : f === 'npc' ? 'npc' : f === 'skull' ? 'skull' : f === 'party' ? 'party' : 'players'}.png`} alt={label} />
            </button>
          ))}
          <button class="bl-sort" title="Ordenar">Sort by display time <i /></button>
        </div>
      }
    >
      <div class="bl-box" style={{ height: h + 'px' }}>
      <ul class="bl-list">
        {list.map((e) => (
          <li key={e.id} class={e.target ? 'target' : ''} onClick={(ev) => { if (ev.shiftKey) { props.onLook?.(e.id); return; } if (e.kind !== 'npc') props.onClick?.(e.id); }}
            onDblClick={() => props.onLook?.(e.id)} onContextMenu={(ev) => { ev.preventDefault(); props.onLook?.(e.id); }}>
            <span class="bl-portrait"><Portrait outfit={e.outfit} /></span>
            <span class="bl-info">
              <span class="bl-name">{e.name}{e.skull && <i class={'skull skull-' + e.skull} title={e.skull + ' skull'} />}</span>
              <span class="bl-hp"><i style={{ width: Math.max(0, Math.min(100, e.hpPct)) + '%', background: hpColor(e.hpPct) }} /></span>
            </span>
          </li>
        ))}
      </ul>
      </div>
      <div class="bl-resize" title="Arraste para aumentar ou diminuir a Battle List" onPointerDown={startDrag as any} />
    </Widget>
  );
}
